// ProcessPreferencePage — Research Home (`/process-preference`).
// Mandate launcher → parse & route to Target / Strategic / PE builders; recent lists table,
// company tearsheet, stats + private-market news. Visual restyle matches the Aug 2026 Figma.
import { useQuery } from '@tanstack/react-query';
import {
  homeNewsService,
  processService,
  resultsService,
} from '@/services/api';
import { buildRecentLists } from '@/features/research-home/recentLists';
import ResearchHomeMandate from '@/features/research-home/ResearchHomeMandate';
import ResearchHomeQuickStart from '@/features/research-home/ResearchHomeQuickStart';
import ResearchHomeRecentLists from '@/features/research-home/ResearchHomeRecentLists';
import ResearchHomeTearsheet from '@/features/research-home/ResearchHomeTearsheet';
import ResearchHomeStatsNews from '@/features/research-home/ResearchHomeStatsNews';
import ResearchHomeRunning from '@/features/research-home/ResearchHomeRunning';
import '@/styles/pages/process-preference.css';

export default function ProcessPreferencePage() {

  const { data: active } = useQuery({
    queryKey: ['research-home', 'active-processing'],
    queryFn: () => processService.getActiveProcessing(),
    // 5s only while a run is live. Idle keeps a slow poll so a run started in another tab
    // still surfaces here, instead of the flat 5s this used to spend on an empty home screen.
    refetchInterval: (query) => (query.state.data?.active ? 5000 : 30_000),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });

  const {
    data: news,
    isLoading: newsLoading,
    isError: newsError,
  } = useQuery({
    queryKey: ['research-home', 'news'],
    queryFn: () => homeNewsService.getNews(),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const listOpts = { page: 1, sortBy: 'newest', timeRange: 'all', userFilter: 'me' } as const;

  const {
    data: targetRes,
    isLoading: targetLoading,
    isError: targetError,
  } = useQuery({
    queryKey: ['research-home', 'results', 'target-list'],
    queryFn: () => resultsService.list('target-list', listOpts),
    staleTime: 60 * 1000,
  });
  const {
    data: strategicRes,
    isLoading: strategicLoading,
    isError: strategicError,
  } = useQuery({
    queryKey: ['research-home', 'results', 'strategic-buyer'],
    queryFn: () => resultsService.list('strategic-buyer', listOpts),
    staleTime: 60 * 1000,
  });
  const {
    data: fvRes,
    isLoading: fvLoading,
    isError: fvError,
  } = useQuery({
    queryKey: ['research-home', 'results', 'fv-results'],
    queryFn: () => resultsService.list('fv-results', listOpts),
    staleTime: 60 * 1000,
  });

  const listsLoading = targetLoading || strategicLoading || fvLoading;
  const allListsError = targetError && strategicError && fvError;

  const targetTotal = targetRes?.pagination.totalResults ?? 0;
  const strategicTotal = strategicRes?.pagination.totalResults ?? 0;
  const fvTotal = fvRes?.pagination.totalResults ?? 0;
  const totalLists = targetTotal + strategicTotal + fvTotal;
  const buyerLists = strategicTotal + fvTotal;

  const recentLists = buildRecentLists(targetRes?.results, strategicRes?.results, fvRes?.results);
  const companiesInRecent = recentLists.reduce((sum, r) => sum + (r.count || 0), 0);

  const isRunning = Boolean(active?.active && active.processId && !active.completed);
  const newsItems = news?.items ?? [];

  const fmt = (value: number, errored: boolean): string => (errored ? '—' : value.toLocaleString());
  const metrics = [
    {
      label: 'Lists generated',
      display: fmt(totalLists, targetError || strategicError || fvError),
    },
    {
      label: 'Companies',
      display: fmt(companiesInRecent, allListsError),
    },
    {
      label: 'Buyer lists',
      display: fmt(buyerLists, strategicError || fvError),
    },
    {
      label: 'Target lists',
      display: fmt(targetTotal, targetError),
    },
  ];

  return (
    <div className="rh-page">
      <header className="rh-header">
        <h1 className="rh-title">Buyer and Target Research</h1>
      </header>

      {isRunning && active && <ResearchHomeRunning active={active} />}

      <div className="rh-hero">
        <ResearchHomeMandate />
        <ResearchHomeQuickStart />
      </div>
      <ResearchHomeRecentLists lists={recentLists} loading={listsLoading} errored={allListsError} />
      <ResearchHomeTearsheet />
      <ResearchHomeStatsNews
        metrics={metrics}
        listsLoading={listsLoading}
        news={newsItems}
        newsLoading={newsLoading}
        newsError={newsError}
      />
    </div>
  );
}
